import { ReactNode, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'shared/hooks';

import './styles.scss';
import { Portal } from 'react-portal';

type UiTooltipPosition = "top" | "bottom" | "left" | "right";

interface UiTooltipProps {
    content: ReactNode;
    position?: UiTooltipPosition;
    delay?: number;
    className?: string;
    contentClassName?: string;
    children: ReactNode;
    disabled?: boolean;
    maxWidth?: number | string;
    zIndex?: number;
}

export const UiTooltip = observer(({
    content,
    position = 'top',
    delay = 200,
    className = '',
    contentClassName = '',
    children,
    disabled = false,
    maxWidth = 300,
    zIndex = 99999
}: UiTooltipProps) => {

    const store = useStore(() => ({
        isVisible: false,
        isReady: false,
        activePosition: position,
        alignmentOffset: 0,
        coords: { top: 0, left: 0 }
    }));

    const wrapperRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const showTooltip = () => {
        if (disabled) return;
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            store.set("isVisible", true);
        }, delay);
    };

    const hideTooltip = () => {
        clearTimeout(timeoutRef.current);
        store.set("isVisible", false);
        store.set("isReady", false);
    };

    const handleClick = (event: MouseEvent) => {
        if (disabled) return;
        event.stopPropagation();
        store.toggle("isVisible");
    };

    const calculateAlignment = (triggerRect: DOMRect, tooltipRect: DOMRect, position: UiTooltipPosition) => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        let offset = 0;

        switch (position) {
            case 'top':
            case 'bottom': {
                const triggerCenterX = triggerRect.left + triggerRect.width / 2;
                const idealLeft = triggerCenterX - tooltipRect.width / 2;
                const right = idealLeft + tooltipRect.width;
                if (idealLeft < 0) offset = -idealLeft;
                else if (right > viewportWidth) offset = viewportWidth - right;
                break;
            }
            case 'left':
            case 'right': {
                const triggerCenterY = triggerRect.top + triggerRect.height / 2;
                const idealTop = triggerCenterY - tooltipRect.height / 2;
                const bottom = idealTop + tooltipRect.height;
                if (idealTop < 0) offset = -idealTop;
                else if (bottom > viewportHeight) offset = viewportHeight - bottom;
                break;
            }
        }
        return offset;
    };

    useEffect(() => {
        if (!store.isVisible) return;

        const updatePosition = () => {
            if (!triggerRef.current || !tooltipRef.current) return;

            const triggerRect = triggerRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let newPosition = position;
            let top = 0, left = 0;

            switch (position) {
                case 'top':
                    if (triggerRect.top - tooltipRect.height < 0) newPosition = 'bottom';
                    break;
                case 'bottom':
                    if (triggerRect.bottom + tooltipRect.height > viewportHeight) newPosition = 'top';
                    break;
                case 'left':
                    if (triggerRect.left - tooltipRect.width < 0) newPosition = 'right';
                    break;
                case 'right':
                    if (triggerRect.right + tooltipRect.width > viewportWidth) newPosition = 'left';
                    break;
            }

            const offset = calculateAlignment(triggerRect, tooltipRect, newPosition);

            switch (newPosition) {
                case 'top':
                    top = triggerRect.top - tooltipRect.height;
                    left = triggerRect.left + triggerRect.width / 2;
                    break;
                case 'bottom':
                    top = triggerRect.bottom;
                    left = triggerRect.left + triggerRect.width / 2;
                    break;
                case 'left':
                    top = triggerRect.top + triggerRect.height / 2;
                    left = triggerRect.left - tooltipRect.width;
                    break;
                case 'right':
                    top = triggerRect.top + triggerRect.height / 2;
                    left = triggerRect.right;
                    break;
            }

            store.set("activePosition", newPosition);
            store.set("alignmentOffset", offset);
            store.set("coords", { top, left });
            store.set("isReady", true);
        };

        const frame = requestAnimationFrame(updatePosition);

        window.addEventListener('resize', updatePosition);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', updatePosition);
        };
    }, [store.isVisible, position]);


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                hideTooltip();
            }
        };
        if (store.isVisible) {
            window.addEventListener('scroll', hideTooltip)
            document.addEventListener('click', handleClickOutside);
            return () => {
                window.removeEventListener('scroll', hideTooltip)
                document.removeEventListener('click', handleClickOutside);
            }
        }
    }, [store.isVisible]);

    const isTouchDevice = typeof window !== 'undefined' && 'ontouchstart' in window;

    const wrapperProps: any = {};
    const triggerProps: any = {};

    if (isTouchDevice) {
        triggerProps.onTouchEnd = handleClick;
    } else {
        wrapperProps.onMouseEnter = showTooltip;
        wrapperProps.onMouseLeave = hideTooltip;
    }

    const tooltipClasses = ['ui-tooltip__dropdown', `ui-tooltip__dropdown--${store.activePosition}`, store.isVisible ? 'ui-tooltip__dropdown--visible' : 'ui-tooltip__dropdown--hidden', contentClassName].filter(Boolean).join(' ');

    const maxWidthStyle = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    const tooltipNode = store.isVisible ? <Portal>
        <div
            ref={tooltipRef}
            className={tooltipClasses}
            style={{
                position: 'fixed',
                top: store.coords.top,
                left: store.coords.left,
                maxWidth: maxWidthStyle,
                transform:
                    store.activePosition === 'top' || store.activePosition === 'bottom'
                        ? `translateX(calc(-50% + ${store.alignmentOffset}px))`
                        : `translateY(calc(-50% + ${store.alignmentOffset}px))`,
                zIndex: zIndex,
                opacity: store.isReady ? 1 : 0,
                visibility: store.isReady ? 'visible' : 'hidden',
                transition: 'opacity 0.15s ease',
            }}
        >
            <div className="ui-tooltip__content">{content}</div>
        </div>
    </Portal> : null;

    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    return (
        <div ref={wrapperRef} className={`ui-tooltip ${className}`} {...wrapperProps}>
            <div ref={triggerRef} className="ui-tooltip__trigger" {...triggerProps}>
                {children}
            </div>
            {tooltipNode}
        </div>
    );
});
